/*******************************************************************************
 * Copyright (c) 2019. BMW Group. All rights reserved.
 ******************************************************************************/

import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild,
  ChangeDetectionStrategy
} from '@angular/core';
import {StatefulComponent} from '../../../shared/stateful/stateful.component';
import {PartNumberChangesAdditionalSpoNumberModel} from '../../models/part-number-changes-additional-spo-number.model';
import {InputTextComponent} from '../../../shared/input/input-text/input-text.component';
import {
    AbstractControl,
    ValidatorFn,
    Validators
} from '@angular/forms';
import {InputMessagesService} from '../../../shared/input/service/input-messages.service';
import {WarningsEnum} from '../../../shared/warnings/warnings.enum';
import {PncTypeEnum} from '../../models/pnc-type-enum/pnc-type.enum';

@Component({
    selector: 'app-part-number-changes-additional-spo-number',
    templateUrl: './part-number-changes-additional-spo-number.component.html',
    styleUrls: ['./part-number-changes-additional-spo-number.component.css'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class PartNumberChangesAdditionalSpoNumberComponent extends StatefulComponent<PartNumberChangesAdditionalSpoNumberModel>
        implements OnInit {

    @Output() newSpoNumberChange = new EventEmitter<PartNumberChangesAdditionalSpoNumberModel>();
    @Output() deleteClick = new EventEmitter<string>();
    @ViewChild('spoNumberInput', {static: true}) spoNumberInput: InputTextComponent;

    public spoNumberInputMessages = new Map<WarningsEnum, string[]>();
    public readonly CYPRESS_TAG = 'pnc-additional-spo-number';
    public readonly LABEL_ADDITIONAL = 'Additional SPO No.';
    public readonly LABEL_NEW = 'SPO No.';
    @Input() isDetailMode: boolean;
    @Input() isSolvedStatus: boolean;
    @Input() isInViewMode: boolean;
    @Input() isDetailSolvedTrial: boolean;
    @Input() additionalIndex?: number;
    @Input() spoNumber: PartNumberChangesAdditionalSpoNumberModel;
    @Input() pncType: string;
    @Input() isSolutionAccepted: boolean;

    validators: { [key: string]: ValidatorFn[] } = {
        spoNumber: [Validators.pattern(/^[sS][0-9]{9}$/)]
    };

    constructor(private inputMessagesService: InputMessagesService) {
        super();
    }

    ngOnInit(): void {
        this.storeState(this.spoNumber);
    }

    get spoNumberLabel(): string {
        return this.additionalIndex != null ? `${this.LABEL_ADDITIONAL} ${this.additionalIndex}` : this.LABEL_NEW;
    }

    get baseCypressTag(): string {
        return this.additionalIndex != null ? `${this.CYPRESS_TAG}-${this.additionalIndex}` : this.CYPRESS_TAG;
    };

    get spoNumberInitialValue(): string {
        return this.isInViewMode && !this.state?.spoNumber ? '-' : this.state.spoNumber;
    }

    get styleClass(): string {
        let styleClass = 'pnc-additional-spo-number';
        if (this.pncType === PncTypeEnum.PLANNER) {
            styleClass += '-planner';
        } else if (this.pncType === PncTypeEnum.AI_CHANGE) {
            styleClass += '-ai';
        }
        return styleClass;
    }

    spoNumberChange(event: string): void {
        this.storeState({
            spoNumber: !event ? null : event.toUpperCase()
        });
    }

    delete(): void {
        this.deleteClick.emit(this.state.id);
    }

    storeState(state?: Partial<PartNumberChangesAdditionalSpoNumberModel>): void {
        super.storeState(state);
        super.storeState({
            isValid: this.spoNumberInput?.isValid ?? false
        });
        this.newSpoNumberChange.emit(this.state);
    }

    manageSpoNumberMessages(spoNumber: AbstractControl): void {
        this.inputMessagesService.invalidInputValueError(spoNumber,
                this.spoNumberInputMessages,
                'SPO number. e.g. S000000000');

        this.spoNumberInputMessages = new Map(this.spoNumberInputMessages);
    }
}
